/**
 * shared/datepick.ts —— 日期选择器的外观增强（2026-09-09）
 *
 * ## 和 `select.ts` 是同一件事的第二例
 * `<input type="date">` 的**日历面板由操作系统绘制**，CSS 一行都管不到：
 * macOS 上是系统蓝的选中块 + 系统自己的「清除 / 今天」，跟全站 navy 五阶完全不搭，
 * 而且要点右边那个小日历图标才弹得出来。这一段把面板换成页面自己画的。
 *
 * ## 做法：同样是包一层，不换实现
 * 原生 `<input type="date">` 留在 DOM 里当**唯一数据源**（视觉隐藏 + 移出 tab 序列），
 * 选中后写回 `inp.value`（仍是 `YYYY-MM-DD`）并派发**冒泡的 input + change**，所以：
 *   - `admin-periods` 里监听 `#cy-end` 的 `input` / `change` 来同步「提交截止」照旧生效；
 *   - `checkins` 里监听 `#ck-f-date` 的 `change` 照旧生效；
 *   - 各处 `root.querySelector('#k-due').value` 读到的还是同样的字符串。
 * 业务代码一行没改。
 *
 * ⚠ 五条纪律：
 * 1. **「今天」一律用 `meta.today()`（服务端北京时间），不用 `new Date()`。**
 *    浏览器在别的时区就会把「今天」画错一天——日期口径在后端（红线 4）。
 * 2. **面板挂 `document.body`（fixed，z-index 85）**，同 `select.ts`：弹窗 `.modal-b`
 *    有 `overflow:auto`，留在原地会被裁掉。两个浮层的层级与关闭时机**必须一致**。
 * 3. **滚动关闭要排掉面板自己那一次**（`scroll` 不冒泡，但 window 上的捕获监听收得到）——
 *    这是 `select.ts` 上线当天踩过的坑，记在 `05` §七 坑 66，别在这里重犯。
 * 4. **只有带 `data-clearable` 的字段才给「清除」**。打卡日期 / 周期起止都是必填，
 *    面板上多一个「清除」是噪音（用户 2026-09-09 明确说清除非必须）；
 *    但 OKR 的截止日期**真的可以留空**，去掉它就没法清空了。
 * 5. **周一起始**：营期周期就是周一到周日（第 1 期 2026-09-14 周一 ~ 09-27 周日），
 *    日历跟着业务走，不跟系统区域设置走。
 */
import { $$ } from './dom.js';
import * as meta from './meta.js';

const WEEK = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
const CHEV_L =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" ' +
  'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M10 3.5 5.5 8 10 12.5"/></svg>';
const CHEV_R =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" ' +
  'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M6 3.5 10.5 8 6 12.5"/></svg>';
const CAL =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" ' +
  'stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<rect x="2.2" y="3.4" width="11.6" height="10.4" rx="1.2"/>' +
  '<path d="M2.2 6.4h11.6M5.4 2.2v2.4M10.6 2.2v2.4"/></svg>';

/* ===================== 日期工具 =====================
   ⚠ 一律走 `YYYY-MM-DD` 字符串与 `new Date(y, m, d)` 的**本地**构造，
     绝不 `new Date('2026-09-09')`——那个按 UTC 解析，东八区会整体偏一天。 */

const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));
const ymd = (y: number, m: number, d: number): string => `${y}-${pad(m + 1)}-${pad(d)}`;

/** 解析 `YYYY-MM-DD`；不合法返回 null。⚠ 月份按 0 起存，和 Date 保持一致 */
function parse(s: string): { y: number; m: number; d: number } | null {
  const t = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s || '').trim());
  if (!t) return null;
  const y = +t[1];
  const m = +t[2] - 1;
  const d = +t[3];
  if (m < 0 || m > 11 || d < 1 || d > daysIn(y, m)) return null;
  return { y, m, d };
}

const daysIn = (y: number, m: number): number => new Date(y, m + 1, 0).getDate();
/** 当月 1 号是星期几，**周一 = 0**（`getDay()` 是周日 = 0，所以要挪一位） */
const firstCol = (y: number, m: number): number => (new Date(y, m, 1).getDay() + 6) % 7;

interface Rec {
  inp: HTMLInputElement;
  box: HTMLElement;
  btn: HTMLButtonElement;
  lb: HTMLElement;
}

let open: { rec: Rec; pan: HTMLElement; y: number; m: number; cur: string } | null = null;

/* ===================== 增强 ===================== */

function sync(rec: Rec): void {
  const v = rec.inp.value;
  /* ⚠ 直接显示 `YYYY-MM-DD`：与后端出参、`meta.today()` 同一种写法。
     原生控件显示的是 `2026/09/09`，全站其余地方都是短横线，不再留第二种日期格式 */
  rec.lb.textContent = v || rec.inp.getAttribute('placeholder') || '选择日期';
  rec.lb.classList.toggle('ph', !v);
  rec.btn.disabled = rec.inp.disabled;
}

function enhance(inp: HTMLInputElement): void {
  if (inp.dataset.dpOn === '1' || !inp.parentNode) return;
  inp.dataset.dpOn = '1';

  const box = document.createElement('div');
  box.className = 'dp-box';
  inp.parentNode.insertBefore(box, inp);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'dp-btn';
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.setAttribute('aria-expanded', 'false');
  const al = inp.getAttribute('aria-label');
  if (al) btn.setAttribute('aria-label', al);
  btn.innerHTML = `<span class="dp-lb"></span><span class="dp-ic">${CAL}</span>`;

  box.appendChild(btn);
  box.appendChild(inp);
  inp.classList.add('dp-native');
  inp.tabIndex = -1;
  inp.setAttribute('aria-hidden', 'true');

  const rec: Rec = { inp, box, btn, lb: btn.firstElementChild as HTMLElement };
  (btn as HTMLButtonElement & { _dp?: Rec })._dp = rec;
  sync(rec);
}

/**
 * 就地增强 root 里所有还没接管的日期输入。
 * ⚠ 与 `select.ts` 的 `enhanceSelects` 同一个理由要显式调用：MutationObserver 的回调是
 *   微任务，比 `openModal` / `render` 同步找首个可聚焦元素晚一步。
 */
export function enhanceDates(root: ParentNode): void {
  $$<HTMLInputElement>('input[type=date]:not([data-dp-on])', root).forEach(enhance);
}

/* ===================== 面板 ===================== */

/** 越界判断：`min` / `max` 是 `YYYY-MM-DD`，字符串比较即可（同宽同格式） */
function outOfRange(rec: Rec, s: string): boolean {
  const { min, max } = rec.inp;
  return (!!min && s < min) || (!!max && s > max);
}

function gridHtml(rec: Rec, y: number, m: number, cur: string): string {
  const today = meta.today();
  const n = daysIn(y, m);
  const lead = firstCol(y, m);
  /* ⚠ 当月之外**留空不填上下月的日期**（用户 2026-09-09 给的样式）：
     补满前后两个月会让「这个月有几天」在扫一眼时读不出来 */
  let h = '';
  for (let i = 0; i < lead; i++) h += '<i class="dp-e"></i>';
  for (let d = 1; d <= n; d++) {
    const s = ymd(y, m, d);
    const off = outOfRange(rec, s);
    h +=
      `<button type="button" class="dp-d${s === cur ? ' on' : ''}${s === today ? ' now' : ''}"` +
      ` data-dpd="${s}" role="gridcell" aria-selected="${s === cur}"` +
      `${off ? ' disabled' : ''}${s === cur ? ' aria-current="date"' : ''}>${d}</button>`;
  }
  return h;
}

function panHtml(rec: Rec, y: number, m: number, cur: string): string {
  /* 换月按钮：翻过去整月都越界就禁掉，免得用户翻到一片灰格子 */
  const py = m === 0 ? y - 1 : y;
  const pm = m === 0 ? 11 : m - 1;
  const ny = m === 11 ? y + 1 : y;
  const nm = m === 11 ? 0 : m + 1;
  const prevEnd = ymd(py, pm, daysIn(py, pm)); // 上个月最后一天
  const nextStart = ymd(ny, nm, 1); // 下个月第一天
  const noPrev = !!rec.inp.min && prevEnd < rec.inp.min;
  const noNext = !!rec.inp.max && nextStart > rec.inp.max;
  const today = meta.today();
  const canToday = !!today && !outOfRange(rec, today);
  return (
    `<div class="dp-h">
      <button type="button" class="dp-nav" data-dpm="-1" aria-label="上个月"${noPrev ? ' disabled' : ''}>${CHEV_L}</button>
      <b class="dp-t">${y}年${m + 1}月</b>
      <button type="button" class="dp-nav" data-dpm="1" aria-label="下个月"${noNext ? ' disabled' : ''}>${CHEV_R}</button>
    </div>
    <div class="dp-w">${WEEK.map((w) => `<s>${w}</s>`).join('')}</div>
    <div class="dp-g" role="grid">${gridHtml(rec, y, m, cur)}</div>
    <div class="dp-f">` +
    /* 纪律 4：必填字段不给「清除」 */
    (rec.inp.hasAttribute('data-clearable')
      ? `<button type="button" class="dp-lk" data-dpa="clear">清除</button>`
      : '') +
    `<button type="button" class="dp-lk r" data-dpa="today"${canToday ? '' : ' disabled'}>今天</button>
    </div>`
  );
}

function place(pan: HTMLElement, btn: HTMLElement): void {
  const r = btn.getBoundingClientRect();
  pan.style.left = '0px';
  pan.style.top = '0px';
  const ph = pan.offsetHeight;
  const pw = pan.offsetWidth;
  const below = window.innerHeight - r.bottom - 10;
  const up = below < ph && r.top - 10 > below;
  const top = up ? Math.max(8, r.top - 5 - ph) : r.bottom + 5;
  let left = r.left;
  if (left + pw > window.innerWidth - 8) left = Math.max(8, window.innerWidth - 8 - pw);
  pan.style.left = `${left}px`;
  pan.style.top = `${top}px`;
}

/**
 * ⚠ 见文件头纪律 3：`scroll` 不冒泡，但 window 上的**捕获**监听照样收得到面板内部的滚动。
 *   面板目前不滚，但这个判断照写——下一次给它加个「选年份」的滚动列表就会踩到。
 */
const onScroll = (e?: Event): void => {
  if (open && e?.target instanceof Node && open.pan.contains(e.target)) return;
  closePan();
};
const onDown = (e: Event): void => {
  const t = e.target;
  if (!open || !(t instanceof Node)) return;
  if (open.pan.contains(t) || open.rec.box.contains(t)) return;
  closePan();
};

function closePan(back?: boolean): void {
  if (!open) return;
  const { rec, pan } = open;
  open = null;
  pan.remove();
  rec.box.classList.remove('dp-open');
  rec.btn.setAttribute('aria-expanded', 'false');
  window.removeEventListener('scroll', onScroll, true);
  window.removeEventListener('resize', onScroll);
  document.removeEventListener('mousedown', onDown, true);
  if (back && document.contains(rec.btn)) rec.btn.focus();
}

function redraw(): void {
  if (!open) return;
  open.pan.innerHTML = panHtml(open.rec, open.y, open.m, open.cur);
  place(open.pan, open.rec.btn);
}

function openPan(rec: Rec): void {
  closePan();
  /* 打开时落在：已填的那个月 → 没填就落在服务端「今天」所在月 → 都没有才用本地当月 */
  const at = parse(rec.inp.value) || parse(meta.today()) || (() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() };
  })();

  const pan = document.createElement('div');
  pan.className = 'dp-pan';
  pan.setAttribute('role', 'dialog');
  pan.setAttribute('aria-label', '选择日期');
  document.body.appendChild(pan);

  open = { rec, pan, y: at.y, m: at.m, cur: rec.inp.value };
  redraw();
  rec.box.classList.add('dp-open');
  rec.btn.setAttribute('aria-expanded', 'true');
  requestAnimationFrame(() => pan.classList.add('in'));

  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onScroll);
  document.addEventListener('mousedown', onDown, true);
}

/** 写回并派发：先关面板再派发，理由同 `select.ts`（change 常触发整页重渲染） */
function commit(rec: Rec, v: string): void {
  const changed = rec.inp.value !== v;
  rec.inp.value = v;
  sync(rec);
  closePan(true);
  if (changed) {
    rec.inp.dispatchEvent(new Event('input', { bubbles: true }));
    rec.inp.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/** 键盘移动高亮：越界不动，跨月自动翻页 */
function move(days: number): void {
  if (!open) return;
  const base = parse(open.cur) || parse(meta.today());
  if (!base) return;
  const dt = new Date(base.y, base.m, base.d + days);
  const s = ymd(dt.getFullYear(), dt.getMonth(), dt.getDate());
  if (outOfRange(open.rec, s)) return;
  open.cur = s;
  open.y = dt.getFullYear();
  open.m = dt.getMonth();
  redraw();
}

/* ===================== 安装 ===================== */

export function installDates(): void {
  enhanceDates(document);
  new MutationObserver((recs) => {
    for (const r of recs) {
      for (const n of Array.from(r.addedNodes)) {
        if (!(n instanceof Element)) continue;
        if (n instanceof HTMLInputElement && n.type === 'date') enhance(n);
        else if (n.querySelector('input[type=date]')) enhanceDates(n);
      }
      if (open && !document.contains(open.rec.box)) closePan();
    }
  }).observe(document.body, { childList: true, subtree: true });

  /* 捕获阶段，理由同 `select.ts`：`events.ts` 注册得早，冒泡阶段拦不住它 */
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target as Element;
      if (!t?.closest) return;

      const btn = t.closest<HTMLElement>('.dp-btn');
      if (btn) {
        const rec = (btn as HTMLElement & { _dp?: Rec })._dp;
        if (!rec || rec.btn.disabled) return;
        e.stopPropagation();
        if (open && open.rec === rec) closePan(true);
        else openPan(rec);
        return;
      }

      if (!open || !t.closest('.dp-pan')) return;
      e.stopPropagation();
      const day = t.closest<HTMLElement>('[data-dpd]');
      if (day) return commit(open.rec, day.dataset.dpd as string);
      const mv = t.closest<HTMLElement>('[data-dpm]');
      if (mv) {
        const step = Number(mv.dataset.dpm);
        const d = new Date(open.y, open.m + step, 1);
        open.y = d.getFullYear();
        open.m = d.getMonth();
        return redraw();
      }
      const act = t.closest<HTMLElement>('[data-dpa]')?.dataset.dpa;
      if (act === 'today') return commit(open.rec, meta.today());
      if (act === 'clear') return commit(open.rec, '');
    },
    true,
  );

  document.addEventListener(
    'keydown',
    (e) => {
      const btn = (document.activeElement as Element)?.closest?.<HTMLElement>('.dp-btn');
      const rec = btn ? (btn as HTMLElement & { _dp?: Rec })._dp : null;
      if (!rec) return;
      const k = e.key;

      if (!open || open.rec !== rec) {
        if (k === 'Enter' || k === ' ' || k === 'ArrowDown') {
          e.preventDefault();
          openPan(rec);
        }
        return;
      }

      if (k === 'Escape') {
        /* 同 select：挡住冒泡，否则 events.ts 会把整个弹窗关掉 */
        e.preventDefault();
        e.stopPropagation();
        closePan(true);
      } else if (k === 'ArrowLeft') {
        e.preventDefault();
        move(-1);
      } else if (k === 'ArrowRight') {
        e.preventDefault();
        move(1);
      } else if (k === 'ArrowUp') {
        e.preventDefault();
        move(-7);
      } else if (k === 'ArrowDown') {
        e.preventDefault();
        move(7);
      } else if (k === 'PageUp' || k === 'PageDown') {
        e.preventDefault();
        const d = new Date(open.y, open.m + (k === 'PageUp' ? -1 : 1), 1);
        open.y = d.getFullYear();
        open.m = d.getMonth();
        redraw();
      } else if (k === 'Enter' || k === ' ') {
        e.preventDefault();
        e.stopPropagation();
        if (open.cur && !outOfRange(rec, open.cur)) commit(rec, open.cur);
      } else if (k === 'Tab') {
        closePan();
      }
    },
    true,
  );
}
